import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, ShieldAlert, X, Leaf, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Citizen sheet — the social hub profile. Tappable from Buzzer: community reviews
   (write your own), visible server bans, and their GreenPages dealer card if they
   run a shop. Reputation you can read before you deal. */

const DEMO_CITIZEN = {
  playercode: 22110, name: "Ada King", handle: "ada_king",
  reviews: { avg: 4.5, count: 2, list: [
    { stars: 5, body: "Sold me a clean laptop, no funny business.", author_name: "Mia Torres", at: "2026-09-12T18:20:00" },
    { stars: 4, body: "Solid cab fares, shows up on time.", author_name: "Ray Dalton", at: "2026-09-10T09:05:00" },
  ] },
  bans: [], dealer: null,
};

function Stars({ n, size = "w-3.5 h-3.5" }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(n) ? "text-amber-300 fill-amber-300" : "text-white/20"}`} />
      ))}
    </span>
  );
}

export default function CitizenSheet({ demo, serverId, token, pc, name, mePc, sfx, onClose }) {
  const [cit, setCit] = useState(null);
  const [stars, setStars] = useState(5);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (demo) { setCit({ ...DEMO_CITIZEN, playercode: pc, name: name || DEMO_CITIZEN.name }); return; }
    api.get(`/nc/servers/${serverId}/city/citizens/${pc}`, { params: { token } })
      .then((r) => setCit(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token, pc, name]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (body.trim().length < 3 || busy) return;
    setBusy(true);
    if (demo) { toast.success("Review posted (demo)"); setBody(""); setBusy(false); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/city/citizens/${pc}/reviews`, { stars, body: body.trim() }, { params: { token } });
      toast.success(r.data.message);
      sfx?.success();
      setBody(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const isMe = pc === mePc;

  return (
    <div data-testid="citizen-sheet" className="absolute inset-0 z-20 bg-black/70 backdrop-blur-sm flex items-end" onClick={onClose}>
      <div className="w-full max-h-[88%] rounded-t-[2rem] bg-zinc-950 border-t border-x border-white/15 flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">Citizen file</p>
          <button data-testid="citizen-close" onClick={onClose} className="ml-auto text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {!cit ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3" style={{ scrollbarWidth: "thin" }}>
            <div className="flex items-center gap-3">
              <span className="w-14 h-14 rounded-2xl bg-sky-400/15 border border-sky-400/30 flex items-center justify-center font-sans font-bold text-xl text-sky-300">
                {(cit.name || "C")[0]}
              </span>
              <div className="flex-1 min-w-0">
                <p data-testid="citizen-name" className="font-sans font-bold text-lg text-white truncate">{cit.name}</p>
                <p className="font-mono text-[9px] text-white/40">@{cit.handle} · #{cit.playercode}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Stars n={cit.reviews.avg} />
                  <span data-testid="citizen-review-avg" className="font-mono text-[9px] text-white/50">
                    {cit.reviews.count ? `${cit.reviews.avg} · ${cit.reviews.count} review${cit.reviews.count > 1 ? "s" : ""}` : "no reviews yet"}
                  </span>
                </div>
              </div>
            </div>

            {cit.bans.length > 0 && (
              <div data-testid="citizen-bans" className="rounded-2xl border border-red-400/40 bg-red-400/10 p-3 flex flex-col gap-1.5">
                {cit.bans.map((b, i) => (
                  <p key={i} className="font-mono text-[9px] text-red-200 flex items-center gap-2">
                    <ShieldAlert className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    BANNED — {b.reason} {b.at && <span className="text-red-300/50">· {String(b.at).slice(0, 10)}</span>}
                  </p>
                ))}
              </div>
            )}

            {cit.dealer && (
              <div data-testid="citizen-dealer" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-3 flex items-center gap-3">
                <Leaf className="w-4 h-4 text-emerald-300 shrink-0" />
                <p className="font-mono text-[9px] text-emerald-200">
                  Runs <span className="font-bold">{cit.dealer.name}</span> on GreenPages — {cit.dealer.rating}★ · {cit.dealer.sales} sales
                </p>
              </div>
            )}

            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">Community reviews</p>
            {cit.reviews.list.length === 0 && <p className="font-mono text-[10px] text-white/30">Nobody's written about them yet.</p>}
            {cit.reviews.list.map((r, i) => (
              <div key={i} data-testid={`citizen-review-${i}`} className="rounded-2xl bg-white/5 border border-white/10 p-3">
                <div className="flex items-center gap-2">
                  <Stars n={r.stars} size="w-3 h-3" />
                  <p className="font-mono text-[8px] text-white/40 ml-auto">{r.author_name} · {String(r.at).slice(0, 10)}</p>
                </div>
                <p className="font-mono text-[10px] text-white/80 mt-1.5 leading-relaxed">{r.body}</p>
              </div>
            ))}

            {!isMe && (
              <div data-testid="citizen-review-form" className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex flex-col gap-2 mt-1">
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50">Deal with them? Leave a word</p>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button key={i} data-testid={`review-star-${i}`} onClick={() => { sfx?.keyTick(); setStars(i); }}>
                      <Star className={`w-6 h-6 transition-colors ${i <= stars ? "text-amber-300 fill-amber-300" : "text-white/20 hover:text-amber-200"}`} />
                    </button>
                  ))}
                </div>
                <textarea data-testid="review-body" value={body} onChange={(e) => setBody(e.target.value)} rows={2} maxLength={280}
                  placeholder="How'd they treat you?"
                  className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none focus:border-amber-300/50 resize-none" />
                <button data-testid="review-submit" onClick={submit} disabled={busy || body.trim().length < 3}
                  className="rounded-full py-2.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black transition-transform active:scale-95 disabled:opacity-40"
                  style={{ backgroundColor: "#e8b93e" }}>Post review</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
